import React, { useState, useEffect, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import { useAuth, useUser, useClerk, AuthenticateWithRedirectCallback } from "@clerk/react";
import axios from "axios";
import ClerkAuthProvider from "@/components/auth/ClerkAuthProvider";
import LoginPage from "@/components/auth/LoginPage";
import AppLayout from "@/components/layout/AppLayout";

// Full Pages
import CalendarPage from "@/pages/CalendarPage";
import BookingsPage from "@/pages/BookingsPage";
import NewBookingPage from "@/pages/NewBookingPage";
import BookingDetailPage from "@/pages/BookingDetailPage";
import EditBookingPage from "@/pages/EditBookingPage";
import FinancialsPage from "@/pages/FinancialsPage";
import InvoicePage from "@/pages/InvoicePage";
import BusinessSettingsPage from "@/pages/BusinessSettingsPage";
import CategoryOnboardingPage from "@/pages/CategoryOnboardingPage";
import HallFinderPage from "@/pages/HallFinderPage";
import RevenuePage from "@/pages/RevenuePage";
import ReportsPage from "@/pages/ReportsPage";
import PrivacyPolicyPage from "@/pages/PrivacyPolicyPage";
import TermsConditionsPage from "@/pages/TermsConditionsPage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL ? `${BACKEND_URL}/api` : "/api";

function AuthenticatedApp({ customToken, onSignOut }) {
  const { getToken } = useAuth();
  const { user } = useUser();
  const navigate = useNavigate();
  const location = useLocation();

  const [profile, setProfile] = useState(null);
  const [businessInfo, setBusinessInfo] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // Setup Axios interceptor
  useEffect(() => {
    axios.defaults.withCredentials = true;
    const interceptor = axios.interceptors.request.use(async (config) => {
      try {
        const storedToken = customToken || localStorage.getItem("bme_custom_token");
        if (storedToken) {
          config.headers.Authorization = `Bearer ${storedToken}`;
        } else if (getToken) {
          try {
            const token = await getToken();
            if (token) {
              config.headers.Authorization = `Bearer ${token}`;
            } else if (user?.id) {
              config.headers.Authorization = `Bearer ${user.id}`;
            }
          } catch (e) {
            if (user?.id) config.headers.Authorization = `Bearer ${user.id}`;
          }
        }
      } catch (err) {
        if (user?.id) config.headers.Authorization = `Bearer ${user.id}`;
      }
      return config;
    });

    return () => axios.interceptors.request.eject(interceptor);
  }, [getToken, user, customToken]);

  const loadUserData = useCallback(async () => {
    try {
      const tokenToUse = customToken || localStorage.getItem("bme_custom_token");
      if (!tokenToUse) return;
      const headers = { Authorization: `Bearer ${tokenToUse}` };

      const [profileRes, bizRes] = await Promise.all([
        axios.get(`${API}/profile`, { headers }),
        axios.get(`${API}/business`, { headers })
      ]);
      if (profileRes.data) setProfile(profileRes.data);
      if (bizRes.data) setBusinessInfo(bizRes.data);
    } catch (err) {
      console.warn("Could not reload live profile/business data:", err);
    }
  }, [customToken]);

  useEffect(() => {
    loadUserData();
  }, [loadUserData]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-rose-50">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 border-4 border-pink-700 border-t-transparent rounded-full animate-spin"></div>
          <span className="text-pink-900 font-bold text-sm tracking-wide">Loading BookMyEvents Portal...</span>
        </div>
      </div>
    );
  }

  return (
    <AppLayout
      profile={profile}
      businessInfo={businessInfo}
      onSignOut={onSignOut}
      onResetCategory={() => navigate("/onboarding/category")}
    >
      <Routes>
        <Route path="/" element={<Navigate to="/calendar" replace />} />
        <Route path="/sign-in/*" element={<Navigate to="/calendar" replace />} />
        <Route path="/sign-up/*" element={<Navigate to="/calendar" replace />} />
        <Route
          path="/calendar"
          element={
            <CalendarPage
              profile={profile}
              businessInfo={businessInfo}
              onRefresh={loadUserData}
            />
          }
        />
        <Route
          path="/bookings"
          element={
            <CalendarPage
              profile={profile}
              businessInfo={businessInfo}
              onRefresh={loadUserData}
            />
          }
        />
        <Route
          path="/bookings/new"
          element={
            <NewBookingPage
              businessInfo={businessInfo}
            />
          }
        />
        <Route
          path="/bookings/:id"
          element={
            <BookingDetailPage
              businessInfo={businessInfo}
            />
          }
        />
        <Route
          path="/bookings/:id/edit"
          element={<EditBookingPage />}
        />
        <Route
          path="/bookings/:id/financials"
          element={
            <FinancialsPage
              businessInfo={businessInfo}
            />
          }
        />
        <Route
          path="/bookings/:id/invoice"
          element={
            <InvoicePage
              businessInfo={businessInfo}
            />
          }
        />
        <Route
          path="/financials"
          element={<RevenuePage businessInfo={businessInfo} />}
        />
        <Route
          path="/revenue"
          element={<RevenuePage businessInfo={businessInfo} />}
        />
        <Route
          path="/reports"
          element={<ReportsPage businessInfo={businessInfo} />}
        />
        <Route
          path="/hall-finder"
          element={<HallFinderPage businessInfo={businessInfo} />}
        />
        <Route
          path="/settings"
          element={
            <BusinessSettingsPage
              businessInfo={businessInfo}
              onRefresh={loadUserData}
            />
          }
        />
        <Route
          path="/privacy-policy"
          element={<PrivacyPolicyPage />}
        />
        <Route
          path="/terms"
          element={<TermsConditionsPage />}
        />
        <Route
          path="/profile"
          element={
            <BusinessSettingsPage
              businessInfo={businessInfo}
              onRefresh={loadUserData}
            />
          }
        />
        <Route
          path="/onboarding/category"
          element={
            <CategoryOnboardingPage
              onCategorySaved={loadUserData}
            />
          }
        />
        <Route path="*" element={<Navigate to="/calendar" replace />} />
      </Routes>
    </AppLayout>
  );
}

function MainAppRoutes() {
  const navigate = useNavigate();
  const { isSignedIn, isLoaded } = useUser();
  const [customToken, setCustomToken] = useState(() => localStorage.getItem("bme_custom_token") || "");
  const { signOut } = useClerk();

  const handleCustomLogin = (token) => {
    localStorage.setItem("bme_custom_token", token);
    localStorage.setItem("bme_category_selected", "Convention Hall");
    setCustomToken(token);
    navigate("/calendar", { replace: true });
  };

  const handleSignOut = () => {
    try {
      localStorage.removeItem("bme_custom_token");
      localStorage.removeItem("bme_category_selected");
      localStorage.clear();
      sessionStorage.clear();
    } catch (e) {}
    try {
      if (signOut) signOut();
    } catch (e) {}
    setCustomToken("");
    navigate("/sign-in", { replace: true });
  };

  const hasAuth = Boolean(customToken || (isLoaded && isSignedIn));
  
  if (hasAuth) {
    return <AuthenticatedApp customToken={customToken} onSignOut={handleSignOut} />;
  }

  return (
    <Routes>
      <Route
        path="/sign-in/*"
        element={
          <LoginPage
            onCustomLogin={handleCustomLogin}
            onSignOut={handleSignOut}
          />
        }
      />
      <Route
        path="/sign-up/*"
        element={
          <LoginPage
            onCustomLogin={handleCustomLogin}
          />
        }
      />
      <Route
        path="/sso-callback"
        element={<AuthenticateWithRedirectCallback />}
      />
      <Route
        path="/*"
        element={
          <Navigate to="/sign-in" replace />}
      />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <ClerkAuthProvider>
        <MainAppRoutes />
      </ClerkAuthProvider>
    </BrowserRouter>
  );
}
